import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

type ControlPredicate = (control: AbstractControl) => boolean;
function defaultControlPredicate(control: AbstractControl) {
  if (control) {
    const value = control.value;
    return value !== null && value !== undefined && value !== '';
  }
  return false;
}

export function isFieldValid(formGroup: FormGroup, field: string): boolean {
  return (formGroup.get(field)?.valid && (formGroup.get(field)?.dirty || formGroup.get(field)?.touched)) || false;
}

export function isFieldInvalid(formGroup: FormGroup, field: string): boolean {
  return (formGroup.get(field)?.invalid && (formGroup.get(field)?.dirty || formGroup.get(field)?.touched)) || false;
}

export function getFieldError(formGroup: FormGroup, field: string): string {
  if (isFieldInvalid(formGroup, field)) {
    const control = formGroup.get(field);
    if (control?.errors) {
      const errorCode = Object.keys(control.errors)[0];
      return `FORMS.ERRORS.${errorCode}`;
    }
  }
  return '';
}

/**
 * Checks if the field should be marked with success or not.
 * @param formGroup The form group where the field belongs to.
 * @param field The field that needs to be checked.
 * @param isCompleteCheck A validator function for complex fields to check
 *  if the field is complete and then should be checked for validity. If this function
 *  is not set, then the performed check is just checking if there is a value present on
 *  the control.value property (this works for simple fields).
 * @returns True or false depending if the field should be marked as success or not.
 */
export function isOptionalFieldSuccess(
  formGroup: FormGroup,
  field: string,
  isCompleteCheck: ControlPredicate = defaultControlPredicate
): boolean {
  const control = formGroup.get(field);
  if (control && isCompleteCheck(control)) {
    return isFieldValid(formGroup, field);
  }
  return false;
}

/**
 * Same as the 'isOptionalFieldSuccess' method but with using the CustomValidators.requiredComplete
 * function to check if the field is complete.
 * @param formGroup The form group where the field belongs to.
 * @param field The field that needs to be checked.
 * @returns True or false depending if the field should be marked as success or not.
 */
export function isOptionalFieldSuccessWhenComplete(formGroup: FormGroup, field: string): boolean {
  return isOptionalFieldSuccess(formGroup, field, control => CustomValidators.requiredComplete(control) === null);
}

export class CustomValidators {
  /**
   * Applies the specified validator if the predicate is satisfied.
   * @param predicate The predicate to check before the validator is applied.
   * @param validator The validator to apply if the predicate is satisfied.
   * @returns A validator function that applies the specified validator if the
   *          predicate is applied.
   */
  static conditional(predicate: ControlPredicate, validator: ValidatorFn | null): ValidatorFn {
    return formControl => {
      if (predicate(formControl)) {
        return validator ? validator(formControl) : null;
      }
      return null;
    };
  }

  /**
   * Requires that all fields from the control value object are different from
   * 'null' or 'undefined'.
   * @param control The control to check its value.
   * @returns 'null' if no error found and, if some fields are not valid, an
   *  object with all the fields of the control's value object with true if the
   *  field was ok and false if not.
   */
  static requiredComplete(control: AbstractControl): ValidationErrors | null {
    const fields: any = {};
    let error = false;
    Object.keys(control.value).forEach(key => {
      if (control.value[key] !== null) {
        fields[key] = true;
      } else {
        fields[key] = false;
        error = true;
      }
    });
    if (!error) {
      return null;
    }
    return {
      required: fields
    };
  }

  /**
   * Requires that the control value is valid and is a future date
   * @param control The control to check its value.
   * @returns 'null' if no error found, { invalidDate: true } if it has
   * an invalid format and { pastDate: true } if it is a past date
   */
  static validDate(control: AbstractControl): ValidationErrors | null {
    const ptDatePattern = /^(((0[1-9]|[12]\d|3[01])\/(0[13578]|1[02])\/((19|[2-9]\d)\d{2}))|((0[1-9]|[12]\d|30)\/(0[13456789]|1[012])\/((19|[2-9]\d)\d{2}))|((0[1-9]|1\d|2[0-8])\/02\/((19|[2-9]\d)\d{2}))|(29\/02\/((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/g;
    if (!control.value.match(ptDatePattern)) {
      return { invalidDate: true };
    }
    return null;
  }

  /**
   * Requires that the control value follows the pattern
   * @param control The control to check its value.
   * @param control The pattern to follow.
   * @returns 'null' if no error found, { invalidPattern: true } if it has
   * an invalid format
   */
  static validPattern(pattern: RegExp, errorCode: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value.match(pattern)) {
        return { [errorCode]: true };
      }
      return null;
    };
  }
}
